import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

const stripUnits = (raw) =>
  String(raw || "")
    .toLowerCase()
    .replace(/[^\d.,/-]+/g, "")
    .trim();

const parseUserNumber = (raw) => parseFraction(stripUnits(raw));

const fractionLatex = (numerator, denominator = 1) =>
  denominator === 1 ? String(numerator) : `\\frac{${numerator}}{${denominator}}`;

function NumberInput({ userAnswer, onChange, disabled, label, suffixLatex, placeholder }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseUserNumber(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label={label}
      suffixLatex={suffixLatex}
      placeholder={placeholder}
    />
  );
}

const taskRegistry = {
  // Długości odpowiadających odcinków zmieniają się k razy.
  SimilarLengthFromScaleTask: {
    generate() {
      const data = pick([
        { k: 2, small: 4, big: 8 },
        { k: 3, small: 5, big: 15 },
        { k: 4, small: 3, big: 12 },
        { k: 2, small: 7, big: 14 },
        { k: 3, small: 6, big: 18 },
      ]);
      return { data };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Dwie figury są podobne w skali <InlineMath math={`k=${data.k}`} />.
          Odpowiadający bok mniejszej figury ma długość{" "}
          <InlineMath math={`${data.small}\\,\\text{cm}`} />. Oblicz długość tego
          boku w większej figurze.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="a" suffixLatex={String.raw`\text{cm}`} placeholder="np. 12" />;
    },
    answer: ({ data }) => data.big,
    answerLabel: "a",
    answerSuffix: "\\,\\mathrm{cm}",
    renderAnswer: ({ data }) => <InlineMath math={`a=${data.big}\\,\\text{cm}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Długości figur podobnych zmieniają się <InlineMath math="k" /> razy.</p>
          <BlockMath math={`a=${data.k}\\cdot${data.small}=${data.big}\\,\\text{cm}`} />
        </div>
      );
    },
  },

  // Obwód, tak samo jak bok, jest wielkością jednowymiarową.
  SimilarPerimeterFromScaleTask: {
    generate() {
      const data = pick([
        { k: 2, small: 9, big: 18 },
        { k: 3, small: 8, big: 24 },
        { k: 4, small: 7, big: 28 },
        { k: 2, small: 13, big: 26 },
        { k: 3, small: 12, big: 36 },
      ]);
      return { data };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Dwa wielokąty są podobne w skali <InlineMath math={`k=${data.k}`} />.
          Obwód mniejszego wielokąta wynosi{" "}
          <InlineMath math={`${data.small}\\,\\text{cm}`} />. Oblicz obwód większego.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="L" suffixLatex={String.raw`\text{cm}`} placeholder="np. 24" />;
    },
    answer: ({ data }) => data.big,
    answerLabel: "L",
    answerSuffix: "\\,\\mathrm{cm}",
    renderAnswer: ({ data }) => <InlineMath math={`L=${data.big}\\,\\text{cm}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Każdy bok rośnie <InlineMath math="k" /> razy, dlatego cały obwód również rośnie <InlineMath math="k" /> razy.</p>
          <BlockMath math={`L=${data.k}\\cdot${data.small}=${data.big}\\,\\text{cm}`} />
        </div>
      );
    },
  },

  // Kierunkowa skala obwodów: k może być większe albo mniejsze od 1.
  DirectedPerimeterFromScaleTask: {
    generate() {
      const data = pick([
        { kNum: 2, kDen: 1, lA: 9, lB: 18 },
        { kNum: 3, kDen: 1, lA: 8, lB: 24 },
        { kNum: 4, kDen: 1, lA: 7, lB: 28 },
        { kNum: 1, kDen: 2, lA: 26, lB: 13 },
        { kNum: 1, kDen: 3, lA: 36, lB: 12 },
        { kNum: 1, kDen: 4, lA: 28, lB: 7 },
      ]);
      return { data: { ...data, k: data.kNum / data.kDen } };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Figura <InlineMath math="B" /> jest podobna do figury <InlineMath math="A" /> w skali{" "}
          <InlineMath math={`k=${fractionLatex(data.kNum, data.kDen)}`} />. Obwód figury{" "}
          <InlineMath math="A" /> wynosi <InlineMath math={`${data.lA}\\,\\text{cm}`} />.
          Oblicz obwód figury <InlineMath math="B" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="L_B" suffixLatex={String.raw`\text{cm}`} placeholder="np. 13" />;
    },
    answer: ({ data }) => data.lB,
    answerLabel: "L_B",
    answerSuffix: "\\,\\mathrm{cm}",
    renderAnswer: ({ data }) => <InlineMath math={`L_B=${data.lB}\\,\\text{cm}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Zapis „figura <InlineMath math="B" /> w skali <InlineMath math="k" /> do figury <InlineMath math="A" />” oznacza, że obwód <InlineMath math="A" /> mnożymy przez <InlineMath math="k" />.</p>
          <BlockMath math={`L_B=${fractionLatex(data.kNum, data.kDen)}\\cdot${data.lA}=${data.lB}\\,\\text{cm}`} />
        </div>
      );
    },
  },

  // Dwa obwody -> skala długości k.
  ScaleFromPerimeterRatioTask: {
    generate() {
      const data = pick([
        { lA: 12, lB: 24, kNum: 2, kDen: 1 },
        { lA: 15, lB: 45, kNum: 3, kDen: 1 },
        { lA: 14, lB: 56, kNum: 4, kDen: 1 },
        { lA: 36, lB: 18, kNum: 1, kDen: 2 },
        { lA: 33, lB: 11, kNum: 1, kDen: 3 },
        { lA: 28, lB: 7, kNum: 1, kDen: 4 },
      ]);
      return { data: { ...data, k: data.kNum / data.kDen } };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Obwód figury <InlineMath math="A" /> wynosi <InlineMath math={`${data.lA}\\,\\text{cm}`} />,
          a obwód podobnej figury <InlineMath math="B" /> wynosi <InlineMath math={`${data.lB}\\,\\text{cm}`} />.
          Oblicz skalę podobieństwa figury <InlineMath math="B" /> do figury <InlineMath math="A" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="k" placeholder="np. 3" />;
    },
    answer: ({ data }) => data.k,
    answerLabel: "k",
    answerSuffix: "",
    renderAnswer: ({ data }) => <InlineMath math={`k=${fractionLatex(data.kNum, data.kDen)}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Obwód jest wielkością liniową, dlatego stosunek obwodów jest równy skali <InlineMath math="k" />.</p>
          <BlockMath math={`k=\\frac{L_B}{L_A}=\\frac{${data.lB}}{${data.lA}}=${fractionLatex(data.kNum, data.kDen)}`} />
        </div>
      );
    },
  },

  // Dwa obwody wyznaczają k, a następnie k^2 przenosi pole.
  AreaFromPerimeterRatioTask: {
    generate() {
      const data = pick([
        { kNum: 2, kDen: 1, lA: 12, lB: 24, pA: 7, pB: 28 },
        { kNum: 3, kDen: 1, lA: 10, lB: 30, pA: 4, pB: 36 },
        { kNum: 4, kDen: 1, lA: 8, lB: 32, pA: 3, pB: 48 },
        { kNum: 1, kDen: 2, lA: 30, lB: 15, pA: 36, pB: 9 },
        { kNum: 1, kDen: 3, lA: 42, lB: 14, pA: 54, pB: 6 },
        { kNum: 1, kDen: 4, lA: 32, lB: 8, pA: 48, pB: 3 },
      ]);
      return { data: { ...data, k: data.kNum / data.kDen } };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Obwód figury <InlineMath math="A" /> wynosi <InlineMath math={`${data.lA}\\,\\text{cm}`} />,
          a obwód podobnej figury <InlineMath math="B" /> wynosi <InlineMath math={`${data.lB}\\,\\text{cm}`} />.
          Pole figury <InlineMath math="A" /> jest równe <InlineMath math={`${data.pA}\\,\\text{cm}^2`} />.
          Oblicz pole figury <InlineMath math="B" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="P" suffixLatex={String.raw`\text{cm}^2`} placeholder="np. 28" />;
    },
    answer: ({ data }) => data.pB,
    answerLabel: "P",
    answerSuffix: "\\,\\mathrm{cm}^2",
    renderAnswer: ({ data }) => <InlineMath math={`P_B=${data.pB}\\,\\text{cm}^2`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Z obwodów wyznaczamy skalę długości.</p>
          <BlockMath math={`k=\\frac{L_B}{L_A}=\\frac{${data.lB}}{${data.lA}}=${fractionLatex(data.kNum, data.kDen)}`} />
          <p><strong>Krok 2.</strong> Pola zmieniają się <InlineMath math="k^2" /> razy.</p>
          <BlockMath math={`P_B=${data.pA}\\cdot\\left(${fractionLatex(data.kNum, data.kDen)}\\right)^2=${data.pB}\\,\\text{cm}^2`} />
        </div>
      );
    },
  },

  // 1) Skala długości k -> stosunek pól = k^2. Dane pole mniejszej + skala -> pole większej.
  AreaFromScaleTask: {
    generate() {
      return {
        data: pick([
          { k: 2, small: 5, big: 20 },
          { k: 3, small: 4, big: 36 },
          { k: 2, small: 7, big: 28 },
          { k: 3, small: 6, big: 54 },
          { k: 4, small: 3, big: 48 },
        ]),
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Dwie figury są podobne w skali <InlineMath math={`k=${data.k}`} />. Pole mniejszej
          figury wynosi <InlineMath math={`${data.small}\\,\\text{cm}^2`} />. Oblicz pole
          większej figury.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <NumberInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="P"
          suffixLatex={String.raw`\text{cm}^2`}
          placeholder="np. 20"
        />
      );
    },
    answer: ({ data }) => data.big,
    answerLabel: "P",
    answerSuffix: "\\,\\mathrm{cm}^2",
    renderAnswer: ({ data }) => <InlineMath math={`P=${data.big}\\,\\text{cm}^2`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Pola figur podobnych są w stosunku <InlineMath math="k^2" />.</p>
          <BlockMath math={`k^2=${data.k}^2=${data.k * data.k}`} />
          <p><strong>Krok 2.</strong> Pole większej figury jest <InlineMath math={`${data.k * data.k}`} /> razy większe.</p>
          <BlockMath math={`P=${data.small}\\cdot ${data.k * data.k}=${data.big}\\,\\text{cm}^2`} />
        </div>
      );
    },
  },

  // 2) Ze stosunku pól wyznacz skalę: P2/P1 = k^2 -> k = sqrt(...). (perfekcyjne kwadraty)
  ScaleFromAreaRatioTask: {
    generate() {
      const data = pick([
        { pA: 10, pB: 40, ratioNum: 4, ratioDen: 1, kNum: 2, kDen: 1 },
        { pA: 5, pB: 45, ratioNum: 9, ratioDen: 1, kNum: 3, kDen: 1 },
        { pA: 3, pB: 48, ratioNum: 16, ratioDen: 1, kNum: 4, kDen: 1 },
        { pA: 40, pB: 10, ratioNum: 1, ratioDen: 4, kNum: 1, kDen: 2 },
        { pA: 45, pB: 5, ratioNum: 1, ratioDen: 9, kNum: 1, kDen: 3 },
        { pA: 48, pB: 3, ratioNum: 1, ratioDen: 16, kNum: 1, kDen: 4 },
      ]);
      return {
        data: {
          ...data,
          ratio: data.ratioNum / data.ratioDen,
          k: data.kNum / data.kDen,
        },
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Pole figury <InlineMath math="A" /> wynosi <InlineMath math={`${data.pA}\\,\\text{cm}^2`} />,
          a pole podobnej figury <InlineMath math="B" /> wynosi <InlineMath math={`${data.pB}\\,\\text{cm}^2`} />.
          Oblicz skalę podobieństwa figury <InlineMath math="B" /> do figury <InlineMath math="A" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <NumberInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="k"
          placeholder="np. 2"
        />
      );
    },
    answer: ({ data }) => data.k,
    answerLabel: "k",
    answerSuffix: "",
    renderAnswer: ({ data }) => <InlineMath math={`k=${fractionLatex(data.kNum, data.kDen)}`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Stosunek pól figur podobnych to kwadrat skali.</p>
          <BlockMath math={`\\frac{P_B}{P_A}=\\frac{${data.pB}}{${data.pA}}=${fractionLatex(data.ratioNum, data.ratioDen)}=k^2`} />
          <p><strong>Krok 2.</strong> Skala to pierwiastek z tego stosunku.</p>
          <BlockMath math={`k=\\sqrt{${fractionLatex(data.ratioNum, data.ratioDen)}}=${fractionLatex(data.kNum, data.kDen)}`} />
        </div>
      );
    },
  },

  // Odpowiadające krawędzie wyznaczają k, a następnie k^3 przenosi objętość.
  SolidVolumeFromLengthRatioTask: {
    generate() {
      const data = pick([
        { aA: 3, aB: 6, kNum: 2, kDen: 1, vA: 5, vB: 40 },
        { aA: 2, aB: 6, kNum: 3, kDen: 1, vA: 2, vB: 54 },
        { aA: 2, aB: 8, kNum: 4, kDen: 1, vA: 3, vB: 192 },
        { aA: 8, aB: 4, kNum: 1, kDen: 2, vA: 56, vB: 7 },
        { aA: 15, aB: 5, kNum: 1, kDen: 3, vA: 108, vB: 4 },
        { aA: 8, aB: 2, kNum: 1, kDen: 4, vA: 192, vB: 3 },
      ]);
      return { data: { ...data, k: data.kNum / data.kDen } };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Bryły <InlineMath math="A" /> i <InlineMath math="B" /> są podobne. Ich odpowiadające
          krawędzie mają długości <InlineMath math={`a_A=${data.aA}\\,\\text{cm}`} /> i{" "}
          <InlineMath math={`a_B=${data.aB}\\,\\text{cm}`} />. Objętość bryły <InlineMath math="A" />
          wynosi <InlineMath math={`${data.vA}\\,\\text{cm}^3`} />. Oblicz objętość bryły <InlineMath math="B" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} label="V" suffixLatex={String.raw`\text{cm}^3`} placeholder="np. 40" />;
    },
    answer: ({ data }) => data.vB,
    answerLabel: "V",
    answerSuffix: "\\,\\mathrm{cm}^3",
    renderAnswer: ({ data }) => <InlineMath math={`V_B=${data.vB}\\,\\text{cm}^3`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Z odpowiadających krawędzi wyznaczamy skalę.</p>
          <BlockMath math={`k=\\frac{a_B}{a_A}=\\frac{${data.aB}}{${data.aA}}=${fractionLatex(data.kNum, data.kDen)}`} />
          <p><strong>Krok 2.</strong> Objętości zmieniają się <InlineMath math="k^3" /> razy.</p>
          <BlockMath math={`V_B=${data.vA}\\cdot\\left(${fractionLatex(data.kNum, data.kDen)}\\right)^3=${data.vB}\\,\\text{cm}^3`} />
        </div>
      );
    },
  },

  // 3) Bryły podobne: stosunek pól powierzchni = k^2 -> skala k -> objętość rośnie k^3 razy.
  SolidVolumeFromSurfaceRatioTask: {
    generate() {
      const data = pick([
        { sA: 5, sB: 20, ratioNum: 4, ratioDen: 1, kNum: 2, kDen: 1, vA: 5, vB: 40 },
        { sA: 4, sB: 36, ratioNum: 9, ratioDen: 1, kNum: 3, kDen: 1, vA: 2, vB: 54 },
        { sA: 3, sB: 48, ratioNum: 16, ratioDen: 1, kNum: 4, kDen: 1, vA: 3, vB: 192 },
        { sA: 28, sB: 7, ratioNum: 1, ratioDen: 4, kNum: 1, kDen: 2, vA: 56, vB: 7 },
        { sA: 54, sB: 6, ratioNum: 1, ratioDen: 9, kNum: 1, kDen: 3, vA: 108, vB: 4 },
        { sA: 48, sB: 3, ratioNum: 1, ratioDen: 16, kNum: 1, kDen: 4, vA: 192, vB: 3 },
      ]);
      return {
        data: {
          ...data,
          paRatio: data.ratioNum / data.ratioDen,
          k: data.kNum / data.kDen,
        },
      };
    },
    renderTask({ data }) {
      return (
        <div style={{ lineHeight: 1.65, marginBottom: 14 }}>
          Bryły <InlineMath math="A" /> i <InlineMath math="B" /> są podobne. Ich pola powierzchni
          całkowitej wynoszą <InlineMath math={`P_A=${data.sA}\\,\\text{cm}^2`} /> i{" "}
          <InlineMath math={`P_B=${data.sB}\\,\\text{cm}^2`} />. Objętość bryły <InlineMath math="A" />
          jest równa <InlineMath math={`${data.vA}\\,\\text{cm}^3`} />. Oblicz objętość bryły <InlineMath math="B" />.
        </div>
      );
    },
    renderInput(_task, userAnswer, onChange, disabled) {
      return (
        <NumberInput
          userAnswer={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="V"
          suffixLatex={String.raw`\text{cm}^3`}
          placeholder="np. 40"
        />
      );
    },
    answer: ({ data }) => data.vB,
    answerLabel: "V",
    answerSuffix: "\\,\\mathrm{cm}^3",
    renderAnswer: ({ data }) => <InlineMath math={`V_B=${data.vB}\\,\\text{cm}^3`} />,
    renderExplanation({ data }) {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong> Najpierw obliczamy stosunek pól powierzchni.</p>
          <BlockMath math={`\\frac{P_B}{P_A}=\\frac{${data.sB}}{${data.sA}}=${fractionLatex(data.ratioNum, data.ratioDen)}=k^2`} />
          <p><strong>Krok 2.</strong> Skalę długości otrzymujemy przez wyciągnięcie pierwiastka.</p>
          <BlockMath math={`k=\\sqrt{${fractionLatex(data.ratioNum, data.ratioDen)}}=${fractionLatex(data.kNum, data.kDen)}`} />
          <p><strong>Krok 3.</strong> Objętości brył podobnych są w stosunku <InlineMath math="k^3" />.</p>
          <BlockMath math={`V_B=${data.vA}\\cdot\\left(${fractionLatex(data.kNum, data.kDen)}\\right)^3=${data.vB}\\,\\text{cm}^3`} />
        </div>
      );
    },
  },
};

function moduleFor(task) {
  const mod = taskRegistry[task?.type];
  if (!mod) throw new Error(`Nieznany typ zadania (pola/objętości): ${task?.type}`);
  return mod;
}

export function generateTask(typeOrOptions) {
  const taskType = typeof typeOrOptions === "string"
    ? typeOrOptions
    : typeOrOptions?.type || pick(Object.keys(taskRegistry));
  return { type: taskType, ...taskRegistry[taskType].generate() };
}

export const SCALE_AREA_VOLUME_CANONICAL_TASK_TYPES = [
  "SimilarPerimeterFromScaleTask",
  "DirectedPerimeterFromScaleTask",
  "ScaleFromPerimeterRatioTask",
  "AreaFromPerimeterRatioTask",
  "ScaleFromAreaRatioTask",
  "SolidVolumeFromLengthRatioTask",
  "SolidVolumeFromSurfaceRatioTask",
];

export function generateTasks() {
  return SCALE_AREA_VOLUME_CANONICAL_TASK_TYPES.map((type) => generateTask(type));
}

export const polaObjetosciRegistry = Object.fromEntries(
  Object.keys(taskRegistry).map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) =>
        renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (payload) => renderExplanation({ ...payload, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer({ ...task, type }, userAnswer),
    },
  ])
);

export function renderTask(task) {
  return moduleFor(task).renderTask(task);
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return moduleFor(task).renderInput(task, userAnswer, onChange, disabled);
}

export function checkAnswer(task, userAnswer) {
  const mod = moduleFor(task);
  if (mod.checkAnswer) return mod.checkAnswer(task, userAnswer);
  const expected = mod.answer(task);
  const parsed =
    typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
      ? userAnswer.parsed
      : parseUserNumber(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - expected) < 0.01;
}

export function renderAnswer(task) {
  return moduleFor(task).renderAnswer(task);
}

export function renderUserAnswer(task, userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const mod = moduleFor(task);
  if (mod.renderUserAnswer) return mod.renderUserAnswer(userAnswer, task);
  return <InlineMath math={`${formatLatexPreview(raw, mod.answerLabel)}${mod.answerSuffix}`} />;
}

export function renderExplanation(task) {
  return moduleFor(task).renderExplanation(task);
}

export const registry = polaObjetosciRegistry;
